import { Card } from '@johnlewispartnership/wtr-ingredients/ingredients/Card';
import { ProgressIndicator } from '@johnlewispartnership/wtr-ingredients/ingredients/ProgressIndicator';
import classNames from 'classnames';
import orderStatus from 'constants/orderStatus';
import urls from 'constants/urls';
import React, { useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { useWtrSelector } from 'redux/hooks';
import { getDeliveryTrackerSteps } from 'redux/modules/delivery-tracking/selectors/get-delivery-tracker';
import { getStatus } from 'redux/modules/orders/selectors/get-order';

import { trackWismoImpression } from 'analytics/wismo-tracking';
import useDeliveryTrackingPoll from 'components/PendingOrders/OrderCard/hooks/useDeliveryTrackingPoll';
import { getDeliveryInfoByOrderId } from 'redux/modules/delivery-tracking/selectors';
import locator from 'utils/locator';
import { getClientPageIsLoaded } from 'redux/modules/page/selectors/get-client-page-is-loaded';
import styles from './OrderCard.scss';
import OrderCardFooter from './OrderCardFooter/OrderCardFooter';
import OrderDetails from './OrderDetails/OrderDetails';
import OrderStatus from './OrderStatus/OrderStatus';

export interface OrderCardProps {
  orderId: string;
}

const OrderCard = ({ orderId }: OrderCardProps) => {
  const status = useWtrSelector(state => getStatus(state, orderId));
  const deliveryTrackerSteps = useWtrSelector(state => getDeliveryTrackerSteps(state, orderId));
  const isViewOrderPage = !!useMatch(urls.viewOrder);
  const isPaymentFailed = status === orderStatus.PAYMENT_FAILED;
  const deliveryInfo = useWtrSelector(state => getDeliveryInfoByOrderId(state, orderId));
  const isPageLoaded = useWtrSelector(getClientPageIsLoaded);

  useDeliveryTrackingPoll(orderId);

  const { pathname } = locator;
  useEffect(() => {
    if (isPageLoaded && deliveryInfo?.loaded && pathname !== null && pathname !== undefined) {
      trackWismoImpression(deliveryInfo);
    }
  }, [isPageLoaded, deliveryInfo, pathname]);

  if (!orderId) {
    return null;
  }

  return (
    <Card
      data-testid={`order-card-id-${orderId}`}
      className={classNames(styles.orderCard, {
        [styles.paymentFailedCard]: isPaymentFailed,
        [styles.viewOrderPage]: isViewOrderPage,
      })}
      footer={<OrderCardFooter orderId={orderId} />}
    >
      <div className={styles.orderCardContent}>
        <div className={styles.topRow}>
          <OrderStatus orderId={orderId} />
        </div>
        <div className={styles.bottomRow}>
          <OrderDetails orderId={orderId} />
          <div className={styles.deliveryTrackerContainer}>
            <div className={styles.deliveryTracker}>
              {deliveryTrackerSteps && (
                <ProgressIndicator steps={deliveryTrackerSteps} data-testid="wismo-step-tracker" />
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OrderCard;
